import {
  Box,
  Card,
  Stack,
  Dialog,
  Button,
  Skeleton,
  Typography,
  DialogTitle,
  ListItemText,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import EmptyContent from 'src/components/empty-content';
import CenterStack from 'src/components/ui-kit/center-stack';
import { useBoolean } from 'src/hooks/use-boolean';
import { PositionTxType } from 'src/type/position';
import { TransactionType, TransactionStatus } from 'src/type/transaction';
import { UserType } from 'src/type/user';
import { Pagination } from 'src/type/utils';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { fParams, ParamsType } from 'src/utils/format-params';
import { fNumber } from 'src/utils/format-number';
import { txAction } from 'src/sections/overview/report/utils';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  user: UserType;
  selectedItems: PositionTxType[];
  onSelects: (txs: PositionTxType[]) => void;
  productId: string;
  productName: string;
  unitName?: string;
};

export default function TransactionSelectDialog({
  open,
  onClose,
  onSelects,
  user,
  selectedItems,
  productId,
  productName,
  unitName,
}: Props) {
  const [scrollTrigger, isInView] = useInView();
  const [transactions, setTransactions] = useState(Array<PositionTxType>());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const initialize = useBoolean(true);
  const [selectedTxs, setSelectedTxs] = useState<Set<string>>(new Set());

  useEffect(() => {
    load();
  }, [open]);

  useEffect(() => {
    if (isInView && hasMoreData) {
      loadMore();
    }
  }, [isInView, hasMoreData]);

  useEffect(() => {
    if (open) {
      setSelectedTxs(new Set(selectedItems.map((tx) => tx.id)));
    }
  }, [open, selectedItems]);

  const load = async () => {
    try {
      initialize.onTrue();
      const data = await getTransactions({
        page: 1,
        lastDocId: '',
      });

      setTransactions(data.results);
      setCurrentPage(1);
      if (data.results.length < 10) {
        setHasMoreData(false);
      } else {
        setHasMoreData(true);
      }
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  };

  const loadMore = async () => {
    try {
      if (hasMoreData && !initialize.value) {
        const data = await getTransactions({
          page: currentPage + 1,
          lastDocId: transactions[transactions.length - 1]?.id || '',
        });
        if (data.results.length === 0) {
          setHasMoreData(false);
        }
        setTransactions((prev) => [...prev, ...data.results]);
        setCurrentPage((prev) => prev + 1);
      }
    } catch (error) {
      toastError(error);
    }
  };

  const getTransactions = async (params: ParamsType) => {
    const _params = {
      ...params,
      limit: 10,
      type: `${TransactionType.BUY_POSITION},${TransactionType.DEPOSIT}`,
      status: `${TransactionStatus.SUCCESS}`,
      userId: user.uid,
      isWalletPage: true,
      productId,
      ...(unitName && { unitName }),
    };
    const { data } = await axios.get<Pagination<PositionTxType>>(endpoints.transactions.get, {
      params: fParams(_params),
    });
    return data;
  };

  // const onSelectTransaction = (tx: PositionTxType) => {
  //   console.log('onSelectTx', tx);
  //   // onSelect(tx);
  //   // onClose();
  // };

  const toggleSelectTransaction = (tx: PositionTxType) => {
    setSelectedTxs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tx.id)) {
        newSet.delete(tx.id);
      } else {
        newSet.add(tx.id);
      }
      return newSet;
    });
  };

  const confirmSelection = () => {
    const items = transactions.filter((tx) => selectedTxs.has(tx.id));
    onSelects(items);
    onClose();
  };

  const beforeClose = () => {
    setSelectedTxs(new Set());
    onClose();
  };

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="md" fullWidth>
      <DialogTitle>รายการตั๋วทอง {productName}</DialogTitle>

      <DialogContent>
        <Stack spacing={1} sx={{ minHeight: '80vh', pt: 3 }}>
          <TransactionCardSkeleton loading={initialize.value} rows={4}>
            {transactions.map((tx) => (
              <TransactionCard
                key={tx.id}
                tx={tx}
                selected={selectedTxs.has(tx.id)}
                onClick={() => toggleSelectTransaction(tx)}
              />
            ))}

            {!transactions.length && <EmptyContent title="ไม่พบข้อมูล" sx={{ mt: 4 }} />}

            {hasMoreData ? (
              <div ref={scrollTrigger}>
                <Stack spacing={2}>
                  <TransactionCardSkeleton loading rows={1} />
                </Stack>
              </div>
            ) : (
              <CenterStack mt={3}>
                {!!transactions.length && (
                  <Typography variant="caption" color="text.secondary">
                    ทั้งหมด {transactions.length} รายการ
                  </Typography>
                )}
              </CenterStack>
            )}
          </TransactionCardSkeleton>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={beforeClose}>
          ปิด
        </Button>
        <Button variant="contained" onClick={confirmSelection} disabled={selectedTxs.size === 0}>
          ยืนยัน ({selectedTxs.size} รายการ)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// export function TransactionCard({ tx, onClick }: { tx: PositionTxType; onClick: VoidFunction }) {
//   const { id, amount, remainGoldAmount, unitName, type } = tx;
//   return (
//     <Card sx={{ p: 2, cursor: 'pointer' }} onClick={onClick}>
//       <Box sx={{ display: 'flex', alignItems: 'center' }}>
//         <ListItemText
//           disableTypography
//           primary={
//             <Stack direction="row" justifyContent="space-between" alignItems="center">
//               <Typography
//                 color={tx.type === TransactionType.BUY_POSITION ? 'success.main' : 'error'}
//                 variant="subtitle1"
//               >
//                 [ตั๋วทอง] {txAction(type)}
//               </Typography>
//               <Typography variant="body2" noWrap>
//                 {fNumber(amount)} {unitName}
//               </Typography>
//             </Stack>
//           }
//           secondary={
//             <Stack direction="row" justifyContent="space-between" alignItems="center">
//               <Typography variant="caption" color="text.secondary" noWrap>
//                 #{id}
//               </Typography>
//               <Typography variant="caption" color="text.secondary" noWrap>
//                 คงเหลือ {fNumber(remainGoldAmount)} {unitName}
//               </Typography>
//             </Stack>
//           }
//         />
//       </Box>
//     </Card>
//   );
// }

export function TransactionCard({
  tx,
  onClick,
  selected,
  disableClick,
  txWeight,
  isSellFromWalletPage,
}: {
  tx: PositionTxType;
  onClick: VoidFunction;
  selected?: boolean;
  disableClick?: boolean;
  txWeight?: number;
  isSellFromWalletPage?: boolean;
}) {
  const { id, amount, remainGoldAmount, unitName, type, product, ticketNumber } = tx;

  console.log('tx', tx);

  return (
    <Card
      sx={{
        p: 2,
        cursor: disableClick ? 'default' : 'pointer',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'transparent',
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ListItemText
          disableTypography
          primary={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1">[ตั๋วทอง]</Typography>
                <Typography color="success.main" variant="subtitle1">
                  {txAction(type)}
                </Typography>
                <Typography variant="subtitle1">{product?.name || '-'}</Typography>
              </Stack>
              <Typography variant="body2" noWrap>
                {fNumber(amount)} {unitName}
              </Typography>
            </Stack>
          }
          secondary={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary" noWrap>
                {ticketNumber ? `เลขที่ตั๋ว: ${ticketNumber}` : `#${id}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                คงเหลือ{' '}
                {fNumber(
                  isSellFromWalletPage && txWeight ? txWeight + remainGoldAmount : remainGoldAmount
                )}{' '}
                {unitName}
              </Typography>
            </Stack>
          }
        />
      </Box>
    </Card>
  );
}

export function TransactionCardSkeleton({
  rows = 1,
  loading,
  children,
}: {
  rows?: number;
  loading: boolean;
  children?: React.ReactNode;
}) {
  if (loading) {
    const array = Array.from({ length: rows }, (_, index) => index);

    return array.map((index) => (
      <Stack
        spacing={2}
        direction="row"
        alignItems="center"
        key={index}
        sx={{
          p: 2.5,
          alignItems: 'flex-start',
          border: (theme) => `solid 1px ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Stack spacing={1} justifyItems="center">
          <Skeleton sx={{ width: 120, height: 12 }} />
          <Skeleton sx={{ width: 180, height: 12 }} />
        </Stack>
      </Stack>
    ));
  }

  return children;
}
